"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import {
  defaultWalletModalLabels,
  type WalletChainId,
  type WalletModalLabels,
} from "../config"

type WalletContextValue = {
  activeChain: WalletChainId
  setActiveChain: (chain: WalletChainId) => void
  modalOpen: boolean
  modalLabels: WalletModalLabels
  openWalletModal: (labels?: Partial<WalletModalLabels>) => void
  closeWalletModal: () => void
}

const WalletContext = createContext<WalletContextValue | null>(null)

type WalletChainProviderProps = {
  children: ReactNode
  /** App-level localized labels used whenever the modal opens. */
  initialModalLabels?: Partial<WalletModalLabels>
}

export function WalletChainProvider({
  children,
  initialModalLabels,
}: WalletChainProviderProps) {
  const [activeChain, setActiveChainState] = useState<WalletChainId>("ethereum")
  const [modalOpen, setModalOpen] = useState(false)

  const baseModalLabels = useMemo(
    () => ({ ...defaultWalletModalLabels, ...initialModalLabels }),
    [
      initialModalLabels?.connect,
      initialModalLabels?.disconnect,
      initialModalLabels?.network,
      initialModalLabels?.title,
      initialModalLabels?.subtitle,
      initialModalLabels?.detected,
      initialModalLabels?.install,
      initialModalLabels?.openApp,
      initialModalLabels?.scanQr,
      initialModalLabels?.moreWallets,
      initialModalLabels?.otherWallets,
      initialModalLabels?.secure,
      initialModalLabels?.connecting,
      initialModalLabels?.rejected,
      initialModalLabels?.selectChainTitle,
      initialModalLabels?.selectChainSubtitle,
      initialModalLabels?.back,
    ],
  )

  const [modalLabels, setModalLabels] = useState<WalletModalLabels>(baseModalLabels)

  useEffect(() => {
    setModalLabels(baseModalLabels)
  }, [baseModalLabels])

  const setActiveChain = useCallback((chain: WalletChainId) => {
    setActiveChainState(chain)
  }, [])

  const openWalletModal = useCallback(
    (labels?: Partial<WalletModalLabels>) => {
      setModalLabels({ ...baseModalLabels, ...labels })
      setModalOpen(true)
    },
    [baseModalLabels],
  )

  const closeWalletModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  const value = useMemo(
    () => ({
      activeChain,
      setActiveChain,
      modalOpen,
      modalLabels,
      openWalletModal,
      closeWalletModal,
    }),
    [
      activeChain,
      setActiveChain,
      modalOpen,
      modalLabels,
      openWalletModal,
      closeWalletModal,
    ]
  )

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  )
}

export function useWalletChain() {
  const ctx = useContext(WalletContext)
  if (!ctx) {
    throw new Error("useWalletChain must be used within WalletChainProvider")
  }
  return ctx
}
